<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

import __MACROS_useVModel from "/vue-macros/define-models/use-vmodel";
import { watch } from 'vue';
import { type AssetSummaryDtoV2 } from '@/api';
import i18n from '@/i18n';
import SiffletifyDataFooter from '@/components/siffletify/siffletify-data-footer/siffletify-data-footer.vue';
import NoDataAvailablePlaceholder from '@/components/placeholders/NoDataAvailablePlaceholder.vue';
import EntityPreviewCard from '@/components/cards/entity-preview-card/Entity-Preview-Card.vue';
import { DataOptions } from 'vuetify';

interface DataPagination {
  page: number;
  pageCount: number;
  itemsPerPage: number;
  itemsLength: number;
}

type AssetFilterType = 'TABLE_AND_VIEW' | 'DASHBOARD' | 'PIPELINE' | 'GENERIC';

interface ButtonInfo {
  text?: string;
  icon?: string;
  value: AssetFilterType | undefined;
}

interface DataProductsDrawerDataAssetsTabProps {
  assets: AssetSummaryDtoV2[];
  assetsCount: number;
}
const props = defineProps({
  assets: null,
  assetsCount: null,
  value: null
});

interface DataProductsDrawerDataAssetsTabEmits {
  (event: 'update-filter', filter: AssetFilterType | undefined): void;
}
const emits = defineEmits(["update-filter", "input"]);

const { value: options } = __MACROS_useVModel(["value", undefined, "input"]);

const filterType = _ref<AssetFilterType | undefined>();

const items = _computed(() => props.assets || []);
const footerData = _computed(() => ({
  options: {
    page: options.value.page,
    itemsPerPage: options.value.itemsPerPage,
    itemsPerPageOptions: [10, 25, 50, 100],
  } as DataOptions & { itemsPerPageOptions?: number[] },
  pagination: {
    page: options.value.page,
    itemsPerPage: options.value.itemsPerPage,
    pageCount: props.assetsCount === 0 ? 0 : Math.ceil(props.assetsCount / options.value.itemsPerPage),
    itemsLength: props.assetsCount,
  } as DataPagination,
}));

const disablePagination = _computed(() => props.assetsCount === 0);

const updateOptions = (newOptions: { page: number; itemsPerPage: number }) => {
  options.value = {
    ...options.value,
    page: newOptions.page,
    itemsPerPage: newOptions.itemsPerPage,
  };
};

const buttons: Record<string, ButtonInfo> = {
  All: {
    text: i18n.t('common.words.All'),
    value: undefined,
  },
  TABLE_AND_VIEW: {
    text: i18n.t('data_products.right_drawer.btn_tables_and_views'),
    value: 'TABLE_AND_VIEW',
  },
  DASHBOARD: {
    text: i18n.t('data_products.right_drawer.btn_dashboards'),
    value: 'DASHBOARD',
  },
  PIPELINE: {
    text: i18n.t('data_products.right_drawer.btn_pipelines'),
    value: 'PIPELINE',
  },
  GENERIC: {
    icon: 'icon-menu-dots-horizontal',
    value: 'GENERIC',
  },
};

watch(() => filterType.value, (newValue) => {
  options.value.page = 1;
  emits('update-filter', newValue);
});

</script>

<template lang="pug">
v-row( no-gutters )
  v-col( cols="12" ).my-1
    v-btn-toggle( v-model="filterType" dense mandatory )
      template( v-for="button in buttons" )
        v-btn.text-body-2(
          color="secondary" class="custom-secondary"
          text outlined
          :value="button.value"
          :key="button.value")
          v-icon(v-if="button.icon" small) {{ button.icon }}
          span(v-else) {{ button.text }}

  v-col( cols="12" ).mt-6

    v-data-iterator(
      return-object
      :items="items"
      :options.sync="options"
      :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
      :hide-default-footer="true")

      template(v-slot:no-data)
        NoDataAvailablePlaceholder( :text="`${$t('data_products.right_drawer.no_result')}`" )

      template(v-slot:item="itemProps")
        EntityPreviewCard( :entity="itemProps.item" )

      template(v-slot:footer)
        SiffletifyDataFooter(
          :footer="footerData"
          :disable-pagination="disablePagination"
          @update="updateOptions"
        )

</template>

<style lang="scss" scoped>
.v-btn-toggle {
  .v-btn.v-btn--has-bg {
    background-color: var(--v-bgPrimary-base) !important;
  }
}
</style>
